// src/pages/settings/CompanySettings.jsx — the "Company" tab in Settings.
import React, { useEffect, useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../config/api';
import { ACCENT, fromSettings, toPayload, BusinessFields, CurrencyFields, DefaultFields } from './companyForm';

const Card = ({ title, subtitle, children }) => (
  <div style={{ background: '#fff', border: '1.5px solid #f0f0f0', borderRadius: 12, padding: 18, marginBottom: 16 }}>
    <div style={{ fontSize: 15, fontWeight: 700, color: '#111' }}>{title}</div>
    {subtitle && <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2, marginBottom: 12 }}>{subtitle}</div>}
    {!subtitle && <div style={{ height: 12 }} />}
    {children}
  </div>
);

export default function CompanySettings() {
  const queryClient = useQueryClient();
  const [form, setForm] = useState(fromSettings(undefined));
  const [saved, setSaved] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadErr, setUploadErr] = useState('');
  const fileRef = useRef(null);

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: () => api.get('/api/settings').then(r => r.data),
  });
  const { data: paymentMethods = [] } = useQuery({
    queryKey: ['payment-methods'],
    queryFn: () => api.get('/api/payment-methods').then(r => r.data),
  });

  useEffect(() => { if (settings) setForm(fromSettings(settings)); }, [settings]);

  const save = useMutation({
    mutationFn: () => api.put('/api/settings', toPayload(form)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (err) => alert(err.response?.data?.error || 'Error saving settings'),
  });

  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadErr('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/api/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      set('logo_url', res.data.url);
    } catch (err) {
      setUploadErr(err?.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const logoSrc = form.logo_url
    ? (form.logo_url.startsWith('http') ? form.logo_url : `${api.defaults.baseURL || ''}${form.logo_url}`)
    : null;

  return (
    <div style={{ maxWidth: 760, fontFamily: "'DM Sans','Segoe UI',sans-serif" }}>
      <Card title="Business Profile" subtitle="Shown on receipts and across the app">
        {/* Logo upload */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
          <div style={{ width: 72, height: 72, borderRadius: 10, border: `2px dashed ${logoSrc ? ACCENT : '#d1d5db'}`, background: logoSrc ? '#f0fdf8' : '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
            {logoSrc ? <img src={logoSrc} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <span style={{ fontSize: 28, color: '#d1d5db' }}>🖼</span>}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button onClick={() => fileRef.current?.click()} disabled={uploading}
              style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer', opacity: uploading ? 0.5 : 1 }}>
              {uploading ? 'Uploading…' : 'Upload Logo'}
            </button>
            {logoSrc && (
              <button onClick={() => set('logo_url', '')}
                style={{ background: '#fef2f2', color: '#dc2626', border: 'none', borderRadius: 7, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                Remove
              </button>
            )}
            <span style={{ fontSize: 11, color: '#9ca3af' }}>JPG, PNG, WEBP · max 5 MB</span>
          </div>
          <input ref={fileRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
        </div>
        {uploadErr && <div style={{ fontSize: 12, color: '#dc2626', background: '#fef2f2', borderRadius: 7, padding: '6px 10px', marginBottom: 10 }}>{uploadErr}</div>}
        <BusinessFields f={form} set={set} />
      </Card>

      <Card title="Currency" subtitle="Base currency and exchange rate">
        <CurrencyFields f={form} set={set} />
      </Card>

      <Card title="Defaults">
        <DefaultFields f={form} set={set} paymentMethods={paymentMethods} />
      </Card>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => save.mutate()} disabled={save.isPending || !form.name}
          style={{ background: ACCENT, color: '#fff', border: 'none', borderRadius: 9, padding: '11px 24px', fontSize: 14, fontWeight: 600, cursor: 'pointer', opacity: (save.isPending || !form.name) ? 0.5 : 1 }}>
          {save.isPending ? 'Saving…' : 'Save Settings'}
        </button>
        {saved && <span style={{ color: ACCENT, fontWeight: 600, fontSize: 13 }}>✓ Saved</span>}
        {!form.name && <span style={{ color: '#9ca3af', fontSize: 12 }}>Company name is required</span>}
      </div>
    </div>
  );
}
